import { useCallback, useEffect, useMemo, useReducer } from 'react';

import {
  createKnowledgeNote,
  fetchKnowledgeNoteCard,
  updateKnowledgeDocumentSummary,
  updateKnowledgeNote,
  type KnowledgeCardNotePayloadDTO,
} from '@/api/knowledge/api';
import {
  AssetEditorSnapshot,
  type AssetCardSourceType,
  type AssetEditorDraft,
} from '@/models/asset-editor.model';
import { isValidUuid } from '@/utils/uuid';

type SaveStatus = 'idle' | 'saving';

interface AssetEditorState {
  draft: AssetEditorDraft;
  savedSnapshot: AssetEditorSnapshot;
  sourceType: AssetCardSourceType;
  sourceUrl: string | null;
  summaryText: string;
  persistedCardId: string | null;
  editorReady: boolean;
  loadError: string | null;
  saveStatus: SaveStatus;
}

export enum AssetEditorActionType {
  LOAD_START = 'LOAD_START',
  LOAD_SUCCESS = 'LOAD_SUCCESS',
  LOAD_NEW = 'LOAD_NEW',
  LOAD_ERROR = 'LOAD_ERROR',
  TITLE_CHANGED = 'TITLE_CHANGED',
  MARKDOWN_CHANGED = 'MARKDOWN_CHANGED',
  TAGS_CHANGED = 'TAGS_CHANGED',
  EDITOR_HYDRATED = 'EDITOR_HYDRATED',
  SAVE_START = 'SAVE_START',
  SAVE_SUCCESS = 'SAVE_SUCCESS',
  SAVE_ERROR = 'SAVE_ERROR',
}

type AssetEditorAction =
  | { type: AssetEditorActionType.LOAD_START }
  | { type: AssetEditorActionType.LOAD_SUCCESS; payload: KnowledgeCardNotePayloadDTO }
  | { type: AssetEditorActionType.LOAD_NEW }
  | { type: AssetEditorActionType.LOAD_ERROR; payload: string }
  | { type: AssetEditorActionType.TITLE_CHANGED; payload: string }
  | { type: AssetEditorActionType.MARKDOWN_CHANGED; payload: string }
  | { type: AssetEditorActionType.TAGS_CHANGED; payload: string[] }
  | { type: AssetEditorActionType.EDITOR_HYDRATED; payload: string }
  | { type: AssetEditorActionType.SAVE_START }
  | {
      type: AssetEditorActionType.SAVE_SUCCESS;
      payload: { draft: AssetEditorDraft; persistedCardId: string };
    }
  | { type: AssetEditorActionType.SAVE_ERROR };

const EMPTY_DRAFT: AssetEditorDraft = { title: '', markdown: '', tags: [] };

const createEmptyState = (editorReady: boolean): AssetEditorState => ({
  draft: EMPTY_DRAFT,
  savedSnapshot: new AssetEditorSnapshot(EMPTY_DRAFT),
  sourceType: 'note',
  sourceUrl: null,
  summaryText: '',
  persistedCardId: null,
  editorReady,
  loadError: null,
  saveStatus: 'idle',
});

const stateFromCard = (card: KnowledgeCardNotePayloadDTO): AssetEditorState => {
  const sourceType = card.source_type ?? 'note';
  const markdown = sourceType === 'note' ? card.markdown_content : (card.summary_text ?? '');
  const draft = { title: card.title, markdown, tags: [...card.tags] };
  return {
    draft,
    savedSnapshot: new AssetEditorSnapshot(draft),
    sourceType,
    sourceUrl: card.source_url ?? null,
    summaryText: card.summary_text ?? '',
    persistedCardId: card.card_id,
    editorReady: true,
    loadError: null,
    saveStatus: 'idle',
  };
};

/**
 * Pure reducer for the asset editor; handles load, field-change, and save lifecycle actions.
 */
export const assetEditorReducer = (
  state: AssetEditorState,
  action: AssetEditorAction,
): AssetEditorState => {
  switch (action.type) {
    case AssetEditorActionType.LOAD_START:
      return { ...createEmptyState(false) };
    case AssetEditorActionType.LOAD_SUCCESS:
      return stateFromCard(action.payload);
    case AssetEditorActionType.LOAD_NEW:
      return createEmptyState(true);
    case AssetEditorActionType.LOAD_ERROR:
      return { ...createEmptyState(true), loadError: action.payload };
    case AssetEditorActionType.TITLE_CHANGED:
      return { ...state, draft: { ...state.draft, title: action.payload } };
    case AssetEditorActionType.MARKDOWN_CHANGED:
      return { ...state, draft: { ...state.draft, markdown: action.payload } };
    case AssetEditorActionType.TAGS_CHANGED:
      return { ...state, draft: { ...state.draft, tags: [...action.payload] } };
    case AssetEditorActionType.EDITOR_HYDRATED:
      return {
        ...state,
        draft: { ...state.draft, markdown: action.payload },
        savedSnapshot: state.savedSnapshot.withMarkdown(action.payload),
      };
    case AssetEditorActionType.SAVE_START:
      return { ...state, saveStatus: 'saving' };
    case AssetEditorActionType.SAVE_SUCCESS:
      return {
        ...state,
        draft: action.payload.draft,
        savedSnapshot: new AssetEditorSnapshot(action.payload.draft),
        persistedCardId: action.payload.persistedCardId,
        saveStatus: 'idle',
      };
    case AssetEditorActionType.SAVE_ERROR:
      return { ...state, saveStatus: 'idle' };
    default:
      return state;
  }
};

interface PersistAssetArgs {
  accessToken: string;
  clientId: string;
  requestedCardId: string;
  persistedCardId: string | null;
  projectId?: string;
  sourceType: AssetCardSourceType;
  draft: AssetEditorDraft;
}

/**
 * Creates or updates the knowledge card for the given asset, routing to the correct API endpoint
 * based on `sourceType` (note vs. document).
 *
 * @returns The persisted card ID (existing or newly created).
 */
const persistAsset = async (args: PersistAssetArgs): Promise<string> => {
  if (args.sourceType === 'document') {
    if (!args.persistedCardId) {
      throw new Error('Document card is not yet persisted on server');
    }
    await updateKnowledgeDocumentSummary(args.accessToken, args.clientId, args.persistedCardId, {
      project_id: args.projectId ?? null,
      title: args.draft.title.trim(),
      summary_text: args.draft.markdown.trim(),
    });
    return args.persistedCardId;
  }

  if (args.persistedCardId) {
    await updateKnowledgeNote(args.accessToken, args.clientId, args.persistedCardId, {
      project_id: args.projectId ?? null,
      title: args.draft.title.trim(),
      markdown_content: args.draft.markdown.trim(),
      tags: args.draft.tags,
    });
    return args.persistedCardId;
  }

  const created = await createKnowledgeNote(args.accessToken, args.clientId, {
    project_id: args.projectId ?? null,
    ...(isValidUuid(args.requestedCardId) ? { card_id: args.requestedCardId } : {}),
    title: args.draft.title.trim(),
    markdown_content: args.draft.markdown.trim(),
    tags: args.draft.tags,
  });
  return created.card_id;
};

const getStatusLabel = (
  isSaving: boolean,
  isDirty: boolean,
): UseAssetEditorResult['statusLabel'] => {
  if (isSaving) {
    return 'Saving';
  }
  return isDirty ? 'Unsaved' : 'Saved';
};

/** Options for `useAssetEditor`. */
export interface UseAssetEditorOptions {
  assetSessionId: string;
  accessToken?: string | null;
  clientId?: string;
  projectId?: string;
}

/** Result returned by the `save` function exposed by `useAssetEditor`. */
export interface AssetEditorSaveResult {
  saved: boolean;
  cardId?: string;
  error?: string;
}

/** Full return type of `useAssetEditor`. */
export interface UseAssetEditorResult {
  title: string;
  markdown: string;
  tags: string[];
  sourceType: AssetCardSourceType;
  sourceUrl: string | null;
  summaryText: string;
  persistedCardId: string | null;
  editorReady: boolean;
  loadError: string | null;
  isDirty: boolean;
  isSaving: boolean;
  isEditable: boolean;
  canSave: boolean;
  statusLabel: 'Saved' | 'Saving' | 'Unsaved';
  setTitle: (title: string) => void;
  setMarkdown: (markdown: string) => void;
  setTags: (tags: string[]) => void;
  hydrateMarkdown: (markdown: string) => void;
  save: (latestMarkdown?: string) => Promise<AssetEditorSaveResult>;
}

/**
 * Manages the full lifecycle of a knowledge card editor session: loading an existing card (if the
 * session ID is a valid UUID), tracking draft changes, computing dirty/save status, and persisting
 * via the API.
 *
 * @param options.assetSessionId - UUID used as both the route key and the requested card ID.
 * @returns Reactive draft state, computed flags, and field setters plus a `save` action.
 */
export const useAssetEditor = ({
  assetSessionId,
  accessToken,
  clientId,
  projectId,
}: UseAssetEditorOptions): UseAssetEditorResult => {
  const shouldLoad =
    isValidUuid(assetSessionId.trim()) && Boolean(accessToken) && Boolean(clientId);
  const [state, dispatch] = useReducer(assetEditorReducer, createEmptyState(!shouldLoad));

  useEffect(() => {
    if (!shouldLoad) {
      dispatch({ type: AssetEditorActionType.LOAD_NEW });
      return undefined;
    }

    let cancelled = false;
    dispatch({ type: AssetEditorActionType.LOAD_START });
    void fetchKnowledgeNoteCard(accessToken!, clientId!, assetSessionId.trim(), projectId ?? null)
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response.found && response.card) {
          dispatch({ type: AssetEditorActionType.LOAD_SUCCESS, payload: response.card });
        } else {
          dispatch({ type: AssetEditorActionType.LOAD_NEW });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const description = error instanceof Error ? error.message : 'Unable to load asset';
          dispatch({ type: AssetEditorActionType.LOAD_ERROR, payload: description });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, assetSessionId, clientId, projectId, shouldLoad]);

  const isDirty = state.savedSnapshot.hasChanges(state.draft);
  const isEditable = state.sourceType === 'note' || state.sourceType === 'document';
  const hasTitle = state.draft.title.trim().length > 0;
  const hasContent = state.draft.markdown.trim().length > 0;
  const isSaving = state.saveStatus === 'saving';
  const canSave = isEditable && isDirty && !isSaving && hasTitle && hasContent;

  const setTitle = useCallback((title: string): void => {
    dispatch({ type: AssetEditorActionType.TITLE_CHANGED, payload: title });
  }, []);
  const setMarkdown = useCallback((markdown: string): void => {
    dispatch({ type: AssetEditorActionType.MARKDOWN_CHANGED, payload: markdown });
  }, []);
  const setTags = useCallback((tags: string[]): void => {
    dispatch({ type: AssetEditorActionType.TAGS_CHANGED, payload: tags });
  }, []);
  const hydrateMarkdown = useCallback((markdown: string): void => {
    dispatch({ type: AssetEditorActionType.EDITOR_HYDRATED, payload: markdown });
  }, []);

  const save = useCallback(
    async (latestMarkdown?: string): Promise<AssetEditorSaveResult> => {
      const draft = { ...state.draft, markdown: latestMarkdown ?? state.draft.markdown };
      const draftIsDirty = state.savedSnapshot.hasChanges(draft);
      if (!isEditable || !draftIsDirty || !draft.title.trim() || !draft.markdown.trim()) {
        return { saved: false };
      }
      if (!accessToken || !clientId) {
        return { saved: false, error: 'Unable to save asset: missing client context' };
      }

      dispatch({ type: AssetEditorActionType.SAVE_START });
      try {
        const persistedCardId = await persistAsset({
          accessToken,
          clientId,
          requestedCardId: assetSessionId.trim(),
          persistedCardId: state.persistedCardId,
          projectId,
          sourceType: state.sourceType,
          draft,
        });
        dispatch({
          type: AssetEditorActionType.SAVE_SUCCESS,
          payload: { draft, persistedCardId },
        });
        return { saved: true, cardId: persistedCardId };
      } catch (error) {
        dispatch({ type: AssetEditorActionType.SAVE_ERROR });
        return {
          saved: false,
          error: error instanceof Error ? error.message : 'Unable to save asset',
        };
      }
    },
    [accessToken, assetSessionId, clientId, isEditable, projectId, state],
  );

  const statusLabel = getStatusLabel(isSaving, isDirty);

  return useMemo(
    () => ({
      ...state.draft,
      sourceType: state.sourceType,
      sourceUrl: state.sourceUrl,
      summaryText: state.summaryText,
      persistedCardId: state.persistedCardId,
      editorReady: state.editorReady,
      loadError: state.loadError,
      isDirty,
      isSaving,
      isEditable,
      canSave,
      statusLabel,
      setTitle,
      setMarkdown,
      setTags,
      hydrateMarkdown,
      save,
    }),
    [
      hydrateMarkdown,
      isDirty,
      isEditable,
      isSaving,
      canSave,
      save,
      setMarkdown,
      setTags,
      setTitle,
      state,
      statusLabel,
    ],
  );
};
